import React, { useEffect, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Pencil, X, Save, Plus } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useProducts } from "../context/ProductsContext";
import { Navigate } from "react-router-dom";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const empty = () => ({
  lookup_key: "",
  name: "",
  number: "",
  tagline: "",
  description: "",
  long_description: "",
  price: 8.99,
  accent: "#B89B5E",
  image: "",
  lifestyle: "",
  notes: [],
  sort_order: 99,
  active: true,
});

export default function AdminProducts() {
  const { user } = useAuth();
  const { refresh } = useProducts();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // product being edited or new
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/products`);
      setRows(data.products || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load products");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  if (user && !user.is_admin) {
    return <Navigate to="/" replace />;
  }

  const save = async () => {
    if (!editing) return;
    setSaving(true);
    try {
      const isNew = !rows.find((r) => r.id === editing.id);
      const body = { ...editing, notes: editing.notes || [] };
      delete body.id;
      delete body.created_at;
      delete body.updated_at;
      if (isNew) {
        await axios.post(`${API}/admin/products`, body);
        toast.success("Product created");
      } else {
        await axios.put(`${API}/admin/products/${editing.id}`, body);
        toast.success("Product updated");
      }
      setEditing(null);
      await load();
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Deactivate this product?")) return;
    try {
      await axios.delete(`${API}/admin/products/${id}`);
      toast.success("Deactivated");
      await load();
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  return (
    <div
      data-testid="admin-products-page"
      className="min-h-screen bg-[color:var(--sr-bg)] pt-32 md:pt-40 pb-24"
    >
      <div className="sr-container">
        <div className="flex items-end justify-between mb-12">
          <div>
            <p className="label-eyebrow text-[color:var(--sr-primary)] mb-3 flex items-center gap-3">
              <span className="w-8 h-px bg-[color:var(--sr-accent)]" /> Admin · Products
            </p>
            <h1 className="font-serif text-5xl md:text-7xl text-[color:var(--sr-primary)] leading-[0.95] tracking-tight">
              The catalog.
            </h1>
          </div>
          <button
            type="button"
            data-testid="admin-new-product"
            onClick={() => setEditing({ ...empty(), id: `_new_${Date.now()}` })}
            className="inline-flex items-center gap-2 px-6 py-3 bg-[color:var(--sr-primary)] text-[color:var(--sr-secondary)] label-eyebrow hover:bg-[color:var(--sr-primary-deep)] transition-colors"
          >
            <Plus size={14} strokeWidth={1.5} /> New product
          </button>
        </div>

        {loading ? (
          <p className="text-sm text-[color:var(--sr-muted)]">Loading…</p>
        ) : (
          <ul
            className="divide-y divide-[color:var(--sr-border-light)] border-y hairline-light"
            data-testid="admin-products-list"
          >
            {rows.map((p) => (
              <li
                key={p.id}
                className={`py-6 flex items-center gap-6 ${!p.active ? "opacity-50" : ""}`}
                data-testid={`admin-row-${p.id}`}
              >
                <div className="w-16 h-20 bg-[color:var(--sr-secondary)] overflow-hidden shrink-0">
                  {p.image && (
                    <img src={p.image} alt="" className="w-full h-full object-cover" />
                  )}
                </div>
                <div className="flex-1">
                  <p className="font-serif text-2xl text-[color:var(--sr-primary)] leading-tight">
                    {p.name}
                  </p>
                  <p className="text-xs text-[color:var(--sr-muted)] mt-1">
                    {p.lookup_key} · N°{p.number} · ${p.price?.toFixed(2)}
                    {!p.active && <span className="ml-2 uppercase tracking-[0.2em] text-[color:var(--sr-accent)]">inactive</span>}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setEditing({ ...p })}
                  data-testid={`admin-edit-${p.id}`}
                  className="p-2 border hairline hover:border-[color:var(--sr-primary)] transition-colors"
                  aria-label="Edit"
                >
                  <Pencil size={14} strokeWidth={1.5} />
                </button>
                <button
                  type="button"
                  onClick={() => remove(p.id)}
                  data-testid={`admin-delete-${p.id}`}
                  className="label-eyebrow text-[color:var(--sr-muted)] hover:text-[color:var(--sr-primary)]"
                >
                  Deactivate
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      {editing && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="fixed inset-0 z-50 bg-[color:var(--sr-primary-deep)]/50 backdrop-blur-md flex items-start justify-center overflow-y-auto py-16 px-4"
          data-testid="admin-editor"
        >
          <motion.div
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            className="w-full max-w-2xl bg-[color:var(--sr-bg)] border hairline p-8 md:p-12"
          >
            <div className="flex items-center justify-between mb-8">
              <p className="label-eyebrow text-[color:var(--sr-primary)]">
                {editing.id?.startsWith("_new_") ? "New product" : "Edit product"}
              </p>
              <button
                onClick={() => setEditing(null)}
                data-testid="admin-editor-close"
                className="p-1"
              >
                <X size={18} strokeWidth={1.25} />
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <F label="Lookup key (Stripe)" testid="f-lookup_key" value={editing.lookup_key} onChange={(v) => setEditing({ ...editing, lookup_key: v })} disabled={!editing.id?.startsWith("_new_")} />
              <F label="Name" testid="f-name" value={editing.name} onChange={(v) => setEditing({ ...editing, name: v })} />
              <F label="Number (e.g. 01)" testid="f-number" value={editing.number} onChange={(v) => setEditing({ ...editing, number: v })} />
              <F label="Price (USD)" testid="f-price" type="number" step="0.01" value={editing.price} onChange={(v) => setEditing({ ...editing, price: parseFloat(v) || 0 })} />
              <F label="Tagline" testid="f-tagline" value={editing.tagline} onChange={(v) => setEditing({ ...editing, tagline: v })} full />
              <F label="Short description" testid="f-description" value={editing.description} onChange={(v) => setEditing({ ...editing, description: v })} multiline full />
              <F label="Long description" testid="f-long_description" value={editing.long_description} onChange={(v) => setEditing({ ...editing, long_description: v })} multiline full />
              <F label="Image path" testid="f-image" value={editing.image} onChange={(v) => setEditing({ ...editing, image: v })} />
              <F label="Lifestyle image path" testid="f-lifestyle" value={editing.lifestyle} onChange={(v) => setEditing({ ...editing, lifestyle: v })} />
              <F label="Accent color" testid="f-accent" value={editing.accent} onChange={(v) => setEditing({ ...editing, accent: v })} />
              <F label="Sort order" testid="f-sort_order" type="number" value={editing.sort_order} onChange={(v) => setEditing({ ...editing, sort_order: parseInt(v) || 0 })} />
              <F label="Notes (comma separated)" testid="f-notes" value={(editing.notes || []).join(", ")} onChange={(v) => setEditing({ ...editing, notes: v.split(",").map((s) => s.trim()).filter(Boolean) })} full />
              <label className="flex items-center gap-2 md:col-span-2 pt-2">
                <input type="checkbox" checked={!!editing.active} onChange={(e) => setEditing({ ...editing, active: e.target.checked })} data-testid="f-active" />
                <span className="text-sm text-[color:var(--sr-primary)]">Active (visible in store)</span>
              </label>
            </div>

            <div className="mt-10 flex items-center justify-end gap-3">
              <button
                type="button"
                onClick={() => setEditing(null)}
                className="label-eyebrow px-6 py-3 text-[color:var(--sr-primary)] hover:opacity-70"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={save}
                disabled={saving}
                data-testid="admin-save-btn"
                className="inline-flex items-center gap-2 px-6 py-3 bg-[color:var(--sr-primary)] text-[color:var(--sr-secondary)] label-eyebrow hover:bg-[color:var(--sr-primary-deep)] transition-colors disabled:opacity-40"
              >
                <Save size={14} strokeWidth={1.5} />
                {saving ? "Saving" : "Save"}
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </div>
  );
}

function F({ label, testid, value, onChange, type = "text", step, disabled, multiline, full }) {
  return (
    <label className={`block ${full ? "md:col-span-2" : ""}`}>
      <span className="label-eyebrow text-[color:var(--sr-muted)] mb-2 block">{label}</span>
      {multiline ? (
        <textarea
          rows={3}
          value={value ?? ""}
          onChange={(e) => onChange(e.target.value)}
          data-testid={testid}
          disabled={disabled}
          className="w-full bg-transparent border hairline focus:border-[color:var(--sr-primary)] outline-none p-3 text-[color:var(--sr-primary)] text-sm font-mono resize-y"
        />
      ) : (
        <input
          type={type}
          step={step}
          value={value ?? ""}
          onChange={(e) => onChange(e.target.value)}
          data-testid={testid}
          disabled={disabled}
          className="w-full bg-transparent border-b hairline focus:border-[color:var(--sr-primary)] outline-none py-2 text-[color:var(--sr-primary)] text-lg font-serif disabled:opacity-50"
        />
      )}
    </label>
  );
}
